import { supabase } from "@/integrations/supabase/client";

/**
 * Serviço para gerenciar uploads de arquivos no Supabase Storage
 */
export interface UploadResponse {
  url: string;
  path: string;
}

export class StorageService {
  private static readonly BUCKET_LOGOS = 'empresas-logos';

  /**
   * Faz upload de uma imagem de logo para o tenant
   * @param tenant_id - ID do tenant
   * @param file - Arquivo de imagem
   * @returns URL pública da imagem
   */
  async uploadLogo(tenant_id: string, file: File): Promise<string> {
    try {
      // Validação do tipo de arquivo
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const allowedExts = ['jpg', 'jpeg', 'png', 'svg', 'webp'];

      if (!fileExt || !allowedExts.includes(fileExt)) {
        throw new Error("Formato de arquivo inválido. Formatos aceitos: JPG, PNG, SVG, WEBP");
      }

      // Validação do tamanho (máx. 2MB)
      const MAX_SIZE = 2 * 1024 * 1024; // 2MB
      if (file.size > MAX_SIZE) {
        throw new Error("O arquivo é muito grande. Tamanho máximo: 2MB");
      }

      // Criar um nome de arquivo único
      const fileName = `${tenant_id}/${Date.now()}.${fileExt}`;

      // Upload para o Supabase Storage
      const { error } = await supabase.storage
        .from(StorageService.BUCKET_LOGOS)
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type
        });

      if (error) throw error;

      // Obter URL pública
      const { data: { publicUrl } } = supabase.storage
        .from(StorageService.BUCKET_LOGOS)
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error: any) {
      console.error("Erro ao fazer upload do logo:", error);
      throw new Error(error.message || "Não foi possível fazer o upload do logo");
    }
  }

  static async upload(file: File, bucket: string): Promise<UploadResponse> {
    try {
      const fileName = `${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(fileName, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      return {
        url: publicUrl,
        path: data.path
      };
    } catch (error: any) {
      throw new Error('Erro ao fazer upload do arquivo: ' + error.message);
    }
  }

  static async delete(path: string, bucket: string = 'empresas-logos'): Promise<void> {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);

      if (error) throw error;
    } catch (error: any) {
      throw new Error('Erro ao excluir arquivo: ' + error.message);
    }
  }

  static async getSignedUrl(path: string, bucket: string = 'empresas-logos'): Promise<string> {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, 3600); // 1 hora

      if (error) throw error;
      return data.signedUrl;
    } catch (error: any) {
      throw new Error('Erro ao gerar URL assinada: ' + error.message);
    }
  }

  static async getPublicUrl(path: string, bucket: string = 'empresas-logos'): Promise<string> {
    try {
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(path);

      return publicUrl;
    } catch (error: any) {
      throw new Error('Erro ao gerar URL pública: ' + error.message);
    }
  }

  static async uploadFile(file: File, subDir: string = 'uploads', bucket: string = 'documentos-clientes'): Promise<string> {
    try {
      const fileName = subDir ? `${subDir}/${Date.now()}-${file.name}` : `${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(fileName, file);

      if (error) throw error;
      return data.path;
    } catch (error: any) {
      throw new Error('Erro ao fazer upload do arquivo: ' + error.message);
    }
  }

  static async deleteFile(path: string, bucket: string = 'documentos-clientes'): Promise<void> {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);

      if (error) throw error;
    } catch (error: any) {
      throw new Error('Erro ao excluir arquivo: ' + error.message);
    }
  }
}

export default new StorageService(); 